/**
 * Native Plugin Initialization
 *
 * Centralizza l'inizializzazione di tutti i plugin Capacitor nativi.
 * Chiamato una sola volta all'avvio dell'app in index.tsx.
 */

import { Capacitor } from '@capacitor/core';
import { logger } from '@deskbay/shared';
import { initSupabase } from '@deskbay/shared/services/supabase/client';
import { createSecureStorageAdapter, migrateFromLocalStorage } from '../storage/secureStorageAdapter';
import { initSecureStorage } from '../storage';

/**
 * Initialize secure storage + Supabase client with native-appropriate storage.
 * MUST run before any Supabase operation or React mount on native.
 */
export async function initSecureAuth(): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      // 1. Migrate existing localStorage tokens to secure storage (one-time)
      await migrateFromLocalStorage();

      // 2. Create secure storage adapter for Supabase auth
      const storageAdapter = await createSecureStorageAdapter();

      // 3. Initialize the storage service (for app-level storage like last_restaurant_id)
      await initSecureStorage();

      // 4. Initialize Supabase with secure storage
      initSupabase(storageAdapter);
      logger.capacitor.info('Supabase initialized with SecureStorage', 'initSecureAuth');
    } else {
      // Web: init with localStorage (default)
      initSupabase();
    }
  } catch (err) {
    logger.capacitor.error('SecureAuth init failed, falling back to localStorage', err, 'initSecureAuth');
    initSupabase(); // Fallback: localStorage
  }
}

export async function initNativePlugins(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  await initStatusBar();
  await initKeyboard();
  await initHaptics();
  await initOneSignal();
  await initAppResumeSessionRefresh();
  await initReviewSessionTracking();
  await initUpdateCheck();
}

/**
 * Listen for app resume (foreground) events and proactively refresh the
 * Supabase session. On native platforms, the WebView's `visibilitychange`
 * event may not fire reliably after the OS suspends/resumes the process,
 * so Supabase's built-in `autoRefreshToken` can miss the window.
 *
 * Hardened implementation:
 * - Guards against concurrent refresh calls (debounce)
 * - Retries on transient network errors with progressive backoff
 * - Restarts the Supabase internal auto-refresh timer after a successful refresh
 * - Skips refresh if access token is still fresh (>60s remaining) to avoid churn
 */
async function initAppResumeSessionRefresh(): Promise<void> {
  try {
    const { App } = await import('@capacitor/app');
    const { supabase } = await import('@deskbay/shared/services/supabase/client');

    // Guard: prevent concurrent refresh calls if app is rapidly toggled
    let isRefreshing = false;

    const attemptRefresh = async (attempt = 1): Promise<void> => {
      if (attempt > 3) {
        logger.capacitor.warn('Session refresh max retries reached — user may need to re-login', 'initAppResumeSessionRefresh');
        return;
      }

      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          logger.capacitor.warn('getSession failed on resume', 'initAppResumeSessionRefresh', { error: sessionError.message, attempt });
          return;
        }

        if (!sessionData?.session) {
          // No session at all — user is not logged in, nothing to refresh
          logger.capacitor.debug('No session on resume, skipping refresh', 'initAppResumeSessionRefresh');
          return;
        }

        // Skip refresh if access token is still fresh (more than 60 seconds remaining)
        const expiresAt = sessionData.session.expires_at ?? 0;
        const secondsRemaining = expiresAt - Math.floor(Date.now() / 1000);
        if (secondsRemaining > 60) {
          logger.capacitor.debug(`Access token still valid (${secondsRemaining}s remaining), skipping refresh`, 'initAppResumeSessionRefresh');
          return;
        }

        logger.capacitor.info(`Token expires in ${secondsRemaining}s, refreshing on resume (attempt ${attempt})`, 'initAppResumeSessionRefresh');

        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) {
          // Distinguish between network errors (retry) and auth errors (don't retry)
          const isNetworkError = refreshError.message?.toLowerCase().includes('network') ||
            refreshError.message?.toLowerCase().includes('fetch') ||
            refreshError.message?.toLowerCase().includes('timeout');

          if (isNetworkError && attempt < 3) {
            const delay = attempt * 2000; // 2s, 4s
            logger.capacitor.warn(`Refresh failed (network), retrying in ${delay}ms`, 'initAppResumeSessionRefresh', { error: refreshError.message, attempt });
            setTimeout(() => attemptRefresh(attempt + 1), delay);
          } else {
            // Auth error (invalid/expired refresh token) — log only, don't force signOut.
            // Supabase's onAuthStateChange will handle this via SIGNED_OUT event if session is truly invalid.
            logger.capacitor.warn('Refresh failed on resume (non-network)', 'initAppResumeSessionRefresh', { error: refreshError.message });
          }
          return;
        }

        if (refreshData?.session) {
          // Restart the Supabase internal auto-refresh timer so it doesn't miss the next window
          try {
            (supabase.auth as unknown as { startAutoRefresh?: () => void }).startAutoRefresh?.();
          } catch { /* non-critical */ }
          logger.capacitor.info('Session refreshed successfully on app resume', 'initAppResumeSessionRefresh');
        }

      } catch (err) {
        const delay = attempt * 2000;
        if (attempt < 3) {
          logger.capacitor.warn(`Unexpected error during refresh, retrying in ${delay}ms`, 'initAppResumeSessionRefresh', { error: err, attempt });
          setTimeout(() => attemptRefresh(attempt + 1), delay);
        } else {
          logger.capacitor.warn('Session refresh on resume failed (non-fatal)', 'initAppResumeSessionRefresh', { error: err });
        }
      }
    };

    await App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        if (isRefreshing) {
          logger.capacitor.debug('Refresh already in progress, skipping duplicate resume event', 'initAppResumeSessionRefresh');
          return;
        }
        isRefreshing = true;
        try {
          await attemptRefresh();
        } finally {
          isRefreshing = false;
        }
      }
    });

    logger.capacitor.info('App resume session refresh listener registered (hardened)', 'initAppResumeSessionRefresh');
  } catch (err) {
    logger.capacitor.warn('App resume listener init failed', 'initAppResumeSessionRefresh', { error: err });
  }
}

async function initStatusBar(): Promise<void> {
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');

    // Detect dark mode
    const isDark = document.documentElement.classList.contains('dark');

    // Set status bar style based on theme
    // Style.Dark = white icons (for dark backgrounds)
    // Style.Light = black icons (for light backgrounds)
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });

    // On Android with edge-to-edge (Android 15+), the status bar must be transparent
    // so the app content extends behind it. Color is set via EdgeToEdge.enable() in MainActivity.
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#00000000' });
    }

    // Listen for theme changes
    const observer = new MutationObserver(async (mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isDarkNow = document.documentElement.classList.contains('dark');
          try {
            await StatusBar.setStyle({ style: isDarkNow ? Style.Dark : Style.Light });
            // Keep transparent on Android — edge-to-edge handles the background
            if (Capacitor.getPlatform() === 'android') {
              await StatusBar.setBackgroundColor({ color: '#00000000' });
            }
          } catch (err) {
            logger.capacitor.warn('Failed to update StatusBar on theme change', 'initStatusBar', { error: err });
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    logger.capacitor.info('StatusBar initialized with theme observer', 'initNativePlugins');
  } catch (err) {
    logger.capacitor.warn('StatusBar init failed', 'initNativePlugins', { error: err });
  }
}

async function initKeyboard(): Promise<void> {
  // ---------------------------------------------------------------
  // STEP 1: registra SUBITO il toggle `keyboard-open`, PRIMA di
  // qualsiasi `await` verso @capacitor/keyboard. I passi di
  // `setAccessoryBarVisible` / `setResizeMode` possono lanciare su
  // alcuni device (plugin non pronto, versione WebView, ecc.) — se
  // fossero prima del listener, il catch unificato sotto silenzierebbe
  // l'errore e il nav resterebbe visibile sopra la tastiera su Android.
  // Mettendolo qui garantiamo che la classe `keyboard-open` funzioni
  // sempre, indipendentemente dall'esito della configurazione Keyboard.
  //
  // Il rilevamento è platform-specific perché i segnali affidabili
  // sono diversi per ciascuna piattaforma:
  //   - iOS  → eventi Capacitor Keyboard `keyboardWillShow/Hide`
  //            (coerenti con Native resize mode, arrivano prima del resize).
  //   - Android → focus dei campi input. In Body resize mode il WebView
  //            viene ridimensionato, quindi `visualViewport.height` e
  //            `window.innerHeight` si riducono entrambi e il delta non
  //            è affidabile; gli eventi del plugin Keyboard a volte non
  //            vengono emessi. Il focus di un input/textarea/contenteditable
  //            è invece l'unica causa per cui la IME si apre su Android.
  // ---------------------------------------------------------------
  const root = document.documentElement;
  const setKeyboardOpen = (open: boolean) => {
    if (open) root.classList.add('keyboard-open');
    else root.classList.remove('keyboard-open');
  };

  if (Capacitor.getPlatform() === 'android') {
    const isTextInput = (el: EventTarget | null): boolean => {
      if (!el || !(el instanceof Element)) return false;
      const tag = el.tagName;
      if (tag === 'TEXTAREA') return true;
      if (tag === 'INPUT') {
        const type = (el as HTMLInputElement).type;
        // input di tipo button/checkbox/… non aprono la tastiera
        return !['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'image', 'color', 'range'].includes(type);
      }
      return (el as HTMLElement).isContentEditable === true;
    };

    document.addEventListener('focusin', (e) => {
      if (isTextInput(e.target)) setKeyboardOpen(true);
    });
    document.addEventListener('focusout', () => {
      // focusout arriva PRIMA di focusin quando si passa da un input
      // a un altro: aspettiamo un tick e ricontrolliamo il nuovo
      // activeElement per evitare flicker della navbar in quel caso.
      window.setTimeout(() => {
        if (!isTextInput(document.activeElement)) setKeyboardOpen(false);
      }, 80);
    });
  }

  // ---------------------------------------------------------------
  // STEP 2: configurazione del plugin @capacitor/keyboard
  // (accessory bar, resize mode, listener iOS).
  // ---------------------------------------------------------------
  try {
    const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');

    // Nasconde la barra accessori su iOS (barra grigia con "Fine" sopra la tastiera)
    await Keyboard.setAccessoryBarVisible({ isVisible: false });

    // Resize mode per piattaforma:
    // - iOS  → Native : il WebView nativo viene ridimensionato dalla tastiera,
    //                   così elementi fixed e `100dvh` si adattano correttamente
    //                   e gli input restano visibili sopra la tastiera.
    // - Android → Body : body element si ridimensiona (il WebView si adatta via
    //                    adjustResize di default). Gli input restano visibili;
    //                    la bottom nav viene nascosta via CSS quando keyboard-open.
    if (Capacitor.getPlatform() === 'ios') {
      await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
      Keyboard.addListener('keyboardWillShow', () => setKeyboardOpen(true));
      Keyboard.addListener('keyboardWillHide', () => setKeyboardOpen(false));
    } else {
      await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
    }

    logger.capacitor.info('Keyboard initialized', 'initNativePlugins', { mode: Capacitor.getPlatform() });
  } catch (err) {
    logger.capacitor.warn('Keyboard init failed', 'initNativePlugins', { error: err });
  }
}

async function initHaptics(): Promise<void> {
  try {
    // Haptics non richiede inizializzazione globale, ma verifichiamo che sia disponibile
    const { Haptics } = await import('@capacitor/haptics');

    // Test di disponibilità (non esegue effettivamente haptic)
    logger.capacitor.info('Haptics available', 'initNativePlugins');
  } catch (err) {
    logger.capacitor.warn('Haptics not available', 'initNativePlugins', { error: err });
  }
}

async function initOneSignal(): Promise<void> {
  try {
    const { oneSignalService } = await import('../oneSignalService');
    await oneSignalService.init();
    logger.capacitor.info('OneSignal initialized', 'initNativePlugins');
  } catch (err) {
    logger.capacitor.warn('OneSignal init failed', 'initNativePlugins', { error: err });
  }
}

/**
 * Incrementa il contatore di sessioni per la logica di recensione nativa.
 * Non-bloccante: un eventuale fallimento non impatta l'avvio dell'app.
 */
async function initReviewSessionTracking(): Promise<void> {
  try {
    const { incrementSessionCount } = await import('./reviewManager');
    await incrementSessionCount();
    logger.capacitor.info('Review session count incremented', 'initNativePlugins');
  } catch (err) {
    logger.capacitor.warn('Review session tracking failed (non-fatal)', 'initNativePlugins', { error: err });
  }
}

/**
 * Pianifica il controllo degli aggiornamenti nativi 5 secondi dopo l'avvio,
 * per non rallentare il bootstrap iniziale (warm-up della cache, throttle
 * di updateManager). Il prompt vero e proprio (iOS e Android) è mostrato da
 * NativeUpdateModal, che esegue il proprio checkNativeUpdate() al mount.
 */
async function initUpdateCheck(): Promise<void> {
  try {
    setTimeout(async () => {
      try {
        const { checkNativeUpdate } = await import('./updateManager');
        await checkNativeUpdate();
      } catch (innerErr) {
        logger.capacitor.warn('Scheduled native update check failed (non-fatal)', 'initNativePlugins', { error: innerErr });
      }
    }, 5000);
    logger.capacitor.info('Native update check scheduled (5s)', 'initNativePlugins');
  } catch (err) {
    logger.capacitor.warn('Update check init failed (non-fatal)', 'initNativePlugins', { error: err });
  }
}
